import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Tabs, Tab, Paper, Typography, IconButton, Stack, Divider } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLocation, useNavigate } from 'react-router';
import { BackButton } from '../../../Shared/BackButton';

type LocationLike = {
  state?: {
    name?: string;
    report?: string;
    [k: string]: any;
  };
};

type Props = {
  locationOverride?: LocationLike;
};

const HtmlPreview: React.FC<Props> = ({ locationOverride }) => {
  // const location = useLocation();
  //   const location = locationOverride;
  const realLocation = useLocation();
  const location = locationOverride ?? realLocation;
  const navigate = useNavigate();
  const htmlContent = location?.state?.report ?? '';
  const iframeRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const IframeRenderer = ({ content }) => {
    useEffect(() => {
      if (iframeRef.current && content) {
        const iframe = iframeRef.current;

        // Write HTML content to iframe
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(content);
        doc.close();

        // Auto-resize iframe based on content
        const resizeIframe = () => {
          try {
            const body = iframe.contentDocument.body;
            const html = iframe.contentDocument.documentElement;
            const height = Math.max(
              body.scrollHeight,
              body.offsetHeight,
              html.clientHeight,
              html.scrollHeight,
              html.offsetHeight
            );
            iframe.style.height = Math.max(height, 600) + 'px';
          } catch (e) {
            iframe.style.height = '800px'; // fallback height
          }
        };

        // Set initial height and resize
        iframe.onload = resizeIframe;
        setTimeout(resizeIframe, 100); // fallback resize
      }
    }, [content]);

    return (
      <iframe
        ref={iframeRef}
        style={{
          width: '100%',
          border: 'none',
          minHeight: '600px',
          backgroundColor: '#fff',
        }}
        title="HTML Report Preview"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    );
  };

  // Direct HTML renderer (alternative method)
  const DirectHTMLRenderer = ({ content }) => {
    // Extract body content from full HTML
    const getBodyContent = (html) => {
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      return bodyMatch ? bodyMatch[1] : html;
    };

    // Extract style content from HTML
    const getStyleContent = (html) => {
      const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
      return styleMatches
        ? styleMatches.map((match) => match.replace(/<\/?style[^>]*>/gi, '')).join('\n')
        : '';
    };

    const bodyContent = getBodyContent(content);
    const styleContent = getStyleContent(content);

    return (
      <Box sx={{ width: '100%', minHeight: '600px' }}>
        {styleContent && <style dangerouslySetInnerHTML={{ __html: styleContent }} />}
        <div dangerouslySetInnerHTML={{ __html: bodyContent }} />
      </Box>
    );
  };

  // Copy content to clipboard
  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(htmlContent);
      // You can add a snackbar or toast notification here
      console.log('HTML content copied to clipboard');
    } catch (err) {
      console.error('Failed to copy content:', err);
    }
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Render different content based on selected tab
  const renderContent = useMemo(() => {
    if (!htmlContent) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No HTML content available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please check if the report data was passed correctly.
          </Typography>
        </Box>
      );
    }

    switch (tabValue) {
      case 0: // Preview tab
        return <IframeRenderer content={htmlContent} />;

      // case 1: // Direct HTML tab (alternative view)
      //     return <DirectHTMLRenderer content={htmlContent} />;

      case 1: // Raw HTML tab
        return (
          <Box sx={{ p: 2, height: '600px', overflow: 'auto' }}>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: '12px',
                fontFamily: 'monospace',
                margin: 0,
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            >
              {htmlContent}
            </pre>
          </Box>
        );

      default:
        return null;
    }
  }, [htmlContent, tabValue]);

  return (
    <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Header with tabs and controls */}
      {/* <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BackButton onClick={() => navigate(-1)} />
                    </Stack>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="HTML preview tabs">
                        <Tab label="Preview" />
                        <Tab label="Raw HTML" />
                    </Tabs>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {htmlContent ? `${Math.round(htmlContent.length / 1024)}KB` : 'No content'}
                        </Typography>
                        <IconButton
                            onClick={handleCopyContent}
                            size="small"
                            disabled={!htmlContent}
                            title="Copy HTML content"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>
            </Box> */}

      <Box sx={{ position: 'relative' }}>{renderContent}</Box>
    </Paper>
  );
};

export default HtmlPreview;
